import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  // PD: debounce + optimistic update — coalesce rapid clicks into a single
  // /cart/change.js POST after the user stops clicking, while keeping the UI
  // live (no pointer-events lock) and the displayed totals in sync on every click.
  static #DEBOUNCE_MS = 350;

  /**
   * Pending desired qty per cart line, keyed by item.key (the stable Shopify
   * line-item key from `data-key`). Keying by key — not by line index — keeps
   * queued changes correct across morphs that re-number lines after a removal.
   * @type {Map<string, number>}
   */
  #pendingByKey = new Map();
  /** @type {ReturnType<typeof setTimeout> | null} */
  #debounceTimer = null;
  /** @type {boolean} true while a /cart/change.js request is in flight */
  #fetchInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * Updates the line and cart totals optimistically, then queues a debounced
   * /cart/change.js write so rapid clicks coalesce into one network call.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const key = this.#keyForLine(line);
    if (!key) return;

    // Optimistic UI: update line total + cart subtotal immediately so the
    // shopper sees their click land without waiting for the network.
    this.#applyOptimisticChange(line, quantity);

    this.#pendingByKey.set(key, quantity);
    this.#scheduleFlush();
  };

  #scheduleFlush() {
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = null;
      this.#flushPending();
    }, CartItemsComponent.#DEBOUNCE_MS);
  }

  /**
   * Sends the next pending line change to the server. Only one fetch is in
   * flight at a time; further pending entries flush automatically when the
   * current request resolves.
   */
  #flushPending() {
    if (this.#fetchInFlight) return; // will retry from updateQuantity's finally()
    if (this.#pendingByKey.size === 0) return;

    const [key, quantity] = this.#pendingByKey.entries().next().value;
    this.#pendingByKey.delete(key);

    // Re-resolve the line index now — a previous morph may have shifted lines
    // after a removal. If the row is gone, the change is moot; drop it.
    const line = this.#lineForKey(key);
    if (line === null) {
      // Try the next pending entry without paying a network round-trip.
      this.#flushPending();
      return;
    }

    this.updateQuantity({ line, quantity, action: quantity === 0 ? 'clear' : 'change' });
  }

  /**
   * Returns the stable Shopify line-item key for a 1-based line index, or
   * null if the row can't be found.
   * @param {number} line
   * @returns {string | null}
   */
  #keyForLine(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!(row instanceof HTMLElement)) return null;
    return row.dataset.key || null;
  }

  /**
   * Returns the current 1-based line index for a Shopify line-item key, or
   * null if no row currently has that key (e.g., it was removed by a morph).
   * @param {string} key
   * @returns {number | null}
   */
  #lineForKey(key) {
    const rows = this.refs.cartItemRows;
    if (!Array.isArray(rows)) return null;
    // Exact key match (default Horizon behaviour).
    for (let i = 0; i < rows.length; i++) {
      if (rows[i] instanceof HTMLElement && rows[i].dataset.key === key) return i + 1;
    }
    // PD: variant-id fallback. When the cart crosses a volume-tier
    // discount threshold (3+/5+/8+), Shopify regenerates the hash portion
    // of every remaining line-item key. Pending optimistic entries keyed by
    // the old hash then fail to resolve, silently dropping the removal and
    // letting the morphed section visually restore the row the user just
    // tried to remove. Variant_id is stable, so fall back to that.
    const variantId = typeof key === 'string' ? key.split(':')[0] : '';
    if (variantId) {
      for (let i = 0; i < rows.length; i++) {
        const rowKey = rows[i] instanceof HTMLElement ? rows[i].dataset.key : '';
        if (rowKey && rowKey.split(':')[0] === variantId) return i + 1;
      }
    }
    return null;
  }

  /**
   * Applies an optimistic price update for a single cart line.
   * Reads the per-unit cents from the row, computes the new line total, and
   * adjusts the cart subtotal by the delta. Falls back silently if the data
   * attributes are missing — the post-fetch morph will still reconcile.
   * @param {number} line - 1-based line index.
   * @param {number} newQty - The new desired quantity.
   */
  #applyOptimisticChange(line, newQty) {
    try {
      const row = this.refs.cartItemRows?.[line - 1];
      if (!(row instanceof HTMLElement)) return;

      const eachCents = Number.parseInt(row.dataset.linePriceEachCents ?? '', 10);
      if (!Number.isFinite(eachCents) || eachCents < 0) return;

      const priceEl = row.querySelector('text-component[data-line-price-cents]');
      if (!(priceEl instanceof HTMLElement)) return;

      const previousLineCents = Number.parseInt(priceEl.dataset.linePriceCents ?? '', 10) || 0;
      const newLineCents = Math.max(0, newQty * eachCents);
      const delta = newLineCents - previousLineCents;

      this.#renderMoney(priceEl, newLineCents);
      priceEl.dataset.linePriceCents = String(newLineCents);

      const cartTotalEl = this.refs.cartTotal;
      if (cartTotalEl instanceof HTMLElement) {
        const currentCents = Number.parseInt(cartTotalEl.dataset.cartTotalCents ?? '', 10);
        if (Number.isFinite(currentCents)) {
          const nextSubtotal = Math.max(0, currentCents + delta);
          this.#renderMoney(cartTotalEl, nextSubtotal);
          cartTotalEl.dataset.cartTotalCents = String(nextSubtotal);
        }
      }
    } catch (error) {
      // Optimistic UI is best-effort. Never throw inside an event handler —
      // the server response will reconcile the displayed values regardless.
      console.error('[cart] optimistic update failed', error);
    }
  }

  /**
   * Writes a formatted money string into a text-component, mirroring the
   * server's money_format + currency so the optimistic value matches the
   * shop's actual rendering. Each money-bearing element carries its own
   * `data-money-format` (line items vs cart total can differ if one has
   * the currency code suffix toggled and the other doesn't).
   * @param {HTMLElement} el - text-component element to update.
   * @param {number} cents - Amount in minor units.
   */
  #renderMoney(el, cents) {
    const wrapper = this.querySelector('.cart-items__wrapper');
    const format =
      el.dataset.moneyFormat ||
      (wrapper instanceof HTMLElement && wrapper.dataset.moneyFormat) ||
      '${{amount}}';
    const currency =
      (wrapper instanceof HTMLElement && wrapper.dataset.currencyCode) ||
      window.Shopify?.currency?.active ||
      'NZD';
    const formatted = formatMoney(cents, format, currency);
    el.setAttribute('value', formatted);
    el.textContent = formatted;
  }

  /**
   * Handles the line item removal (trash button or qty=0).
   * Decrements the cart subtotal optimistically, animates the row out, and
   * queues the server-side removal so it coalesces with any in-flight write.
   * @param {number} line - The 1-based line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    // Optimistically subtract this line's contribution from the cart subtotal.
    this.#applyOptimisticChange(line, 0);

    // Queue the server-side removal — same flow as the qty stepper.
    const key = cartItemRowToRemove.dataset.key;
    if (key) {
      this.#pendingByKey.set(key, 0);
      // Removal is a final intent, not part of an editing burst — flush
      // sooner than the stepper debounce so the server catches up quickly.
      if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
      this.#flushPending();
    } else {
      // Fallback: row missing data-key (shouldn't happen, but be safe).
      this.updateQuantity({ line, quantity: 0, action: 'clear' });
    }

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    this.#fetchInFlight = true;

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();

        // After morph, server-rendered totals are the truth again. Re-apply any
        // optimistic state for lines the shopper has clicked while the request
        // was in flight, so the UI doesn't briefly snap back to the old qty.
        this.#reapplyPendingOptimistic();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#fetchInFlight = false;
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        // If the shopper kept clicking during the request, fire the next one.
        if (this.#pendingByKey.size > 0) this.#flushPending();
      });
  }

  /**
   * Re-applies the latest pending desired qty to the input value + line and
   * cart totals after a morph, so the UI reflects what the shopper has
   * already clicked rather than the now-stale server snapshot.
   */
  #reapplyPendingOptimistic() {
    if (this.#pendingByKey.size === 0) return;
    for (const [key, qty] of this.#pendingByKey.entries()) {
      const line = this.#lineForKey(key);
      if (line === null) continue;
      const selector = this.refs.quantitySelectors?.[line - 1];
      const input = selector instanceof HTMLElement ? selector.querySelector('input') : null;
      if (input instanceof HTMLInputElement && qty > 0) {
        input.value = String(qty);
      }
      this.#applyOptimisticChange(line, qty);

      // PD: if the pending entry is a removal (qty 0) and the morph
      // restored the row in its non-animating state, re-apply the
      // `removing` class so the visual "row sliding out" continues until
      // this entry's own /cart/change.js POST resolves (which will be
      // another morph that removes the row server-side). Without this,
      // the row briefly re-appears between morphs.
      if (qty === 0) {
        const row = this.refs.cartItemRows?.[line - 1];
        if (row instanceof HTMLElement && !row.classList.contains('removing')) {
          row.style.setProperty('--row-height', `${row.clientHeight}px`);
          row.classList.add('removing');
        }
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();

      // External cart updates (e.g. add-to-cart from a product form) overwrite
      // our optimistic line/cart totals. Re-apply any queued stepper changes
      // so the shopper's in-flight clicks aren't visually undone.
      this.#reapplyPendingOptimistic();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
